import {Injectable} from '@angular/core';
import {BehaviorSubject, Observable, Subject} from 'rxjs';
import {filter, shareReplay, startWith, switchMapTo, tap, withLatestFrom} from 'rxjs/operators';

import {LpPaymentRequestDataService} from '../../services/lp/lp-payment-request-data.service';
import DeliveryMethod from '../../dashboard/shared/holding/capital-call/models/DeliveryMethod.enum';
import {AppSettingsService} from '../../services/app-settings.service';
import {PendingPaymentRequestResponse} from '../../shared/models/PendingPaymentRequestResponse.model';
import {TerraUtils} from '../../shared/TerraUtils';
import {UserService} from 'src/app/services/shared/user.service';
import {LpTopNotificationService} from './lp-top-notification.service';

@Injectable({
  providedIn: 'root'
})
export class LpPaymentRequestService {
  private _refreshPendingPaymentRequests$ = new Subject<void>();
  private _isLoadingPaymentRequests$ = new BehaviorSubject<boolean>(false);
  isLoadingPaymentRequests$ = this._isLoadingPaymentRequests$.asObservable();
  refreshPendingPaymentRequests$ = this._refreshPendingPaymentRequests$.asObservable();
  pendingPaymentRequests$ = this._refreshPendingPaymentRequests$.pipe(
    startWith(''),
    withLatestFrom(this.userService.isLoggedIn$),
    filter(([_, isLoggedIn]) => isLoggedIn),
    tap(_ => this._isLoadingPaymentRequests$.next(true)),
    switchMapTo(this.paymentRequestDataService.getPendingPaymentRequests()),
    tap(_ => this._isLoadingPaymentRequests$.next(false)),
    tap(_ => this.notificationsService.shouldRefreshNotifications$.next(true)),
    shareReplay(1)
  );

  constructor(private paymentRequestDataService: LpPaymentRequestDataService,
    private notificationsService: LpTopNotificationService,
    private appSetting: AppSettingsService,
    private userService: UserService) { }

  public refreshPendingPaymentRequests() {
    this._refreshPendingPaymentRequests$.next();
  }

  /**
   * Get a link to download the payment request'S PDF
   * @param paymentToken Payment request token
   */
  public downloadPaymentRequestPdf(paymentToken: string) {
    return this.paymentRequestDataService.downloadPaymentRequestPdf(paymentToken);
  }

  /**
   * Get the available delivery methods for the LP
   * @param paymentToken Payment token
   * @param countryId LP's bank account country id
   */
  public getDeliveryMethodsForCountry(paymentToken: string, countryId: number): Observable<DeliveryMethod> {
    return this.paymentRequestDataService.getDeliveryMethodsForCountry(paymentToken, countryId);
  }

  /**
   * Marks payment requests as seen
   * @param paymentTokens Payments tokens
   */
  public markPaymentRequestsAsViewed(paymentTokens: string[]) {
    return this.paymentRequestDataService.markPaymentRequestsAsViewed(paymentTokens);
  }

  /**
   * Get pending payment requests for holding
   * @param holdingId Holding id
   */
  public getPaymentRequestsForHolding(holdingId: number): Observable<PendingPaymentRequestResponse[]> {
    return this.paymentRequestDataService.getPendingPaymentRequestsForHolding(holdingId).pipe(shareReplay(1));
  }

  /**
   * Scroll to the first payment request
   */
  public scrollToPaymentRequest() {
    TerraUtils.scrollToElementById('payment-requests', true);
  }
}
